import { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import type { OrderDetail, OrderDelivery } from './OrderDetailPanel';
import { useColors } from '../../hooks/useColors';

export interface OrderProofsSectionProps {
  order: OrderDetail;
  deliveries?: OrderDelivery[];
}

interface ProofDef {
  id: string;
  label: string;
  url?: string | null;
  shouldExist: boolean;
  timestamp?: string | null;
  agentName?: string | null;
  isInlineOnly?: boolean;
}

const STATUS_ORDER = [
  'placed',
  'agent_assigned',
  'picked_up',
  'at_facility',
  'in_wash',
  'in_iron',
  'quality_check',
  'ready',
  'out_for_delivery',
  'delivered'
];

const formatRelativeTime = (iso?: string | null) => {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  const ms = Date.now() - parsed.getTime();
  if (ms < 0) return 'just now';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
};

export default function OrderProofsSection({ order, deliveries = [] }: OrderProofsSectionProps) {
  const colors = useColors();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const currentIdx = STATUS_ORDER.indexOf(order.status);
  const isTerminal = order.status === 'cancelled' || order.status === 'returned' || order.status === 'failed';
  
  // Deliveries can come from props or order.deliveries
  const allDeliveries = deliveries.length > 0 ? deliveries : (order.deliveries || []);
  
  const pickupDelivery = allDeliveries.find((d) => d.type === 'pickup');
  const dropDelivery = allDeliveries.find((d) => d.type === 'drop');
  
  const agentNameById = new Map<string, string>();
  allDeliveries.forEach(d => {
    if (d.agent_id && d.agent_name) {
      agentNameById.set(d.agent_id, d.agent_name);
    }
  });

  const getAgentName = (id?: string | null, fallbackName?: string | null) => {
    if (id && agentNameById.has(id)) return agentNameById.get(id);
    if (fallbackName) return fallbackName;
    if (id) return id.substring(0, 8);
    return null;
  };

  // The pickup proof actually lands on the delivery row (verification_photo_url),
  // captured by the agent's item-verification step. orders.pickup_photo_url is
  // only set when the agent attaches a photo to the picked_up transition (rare),
  // so fall back to the delivery photo - otherwise admin shows "No proof captured"
  // even though the agent did capture one.
  const pickupPhotoUrl = order.pickup_photo_url || pickupDelivery?.verification_photo_url || null;

  // Hub-pickup photos. hub_pickup_photo_urls already contains SIGNED urls (the
  // backend signs the legacy single key into it too). Never use the raw
  // hub_pickup_confirmation key here - it would render as a broken image.
  const hubPickupPhotos: string[] = (order.hub_pickup_photo_urls && order.hub_pickup_photo_urls.length)
    ? order.hub_pickup_photo_urls
    : [];
  const hubPickupProofs: ProofDef[] = hubPickupPhotos.length
    ? hubPickupPhotos.map((u, i) => ({
        id: `hub_pickup_confirmation_${i}`,
        label: hubPickupPhotos.length > 1 ? `Hub-pickup photo ${i + 1}` : 'Hub-pickup confirmation',
        url: u,
        shouldExist: true,
        timestamp: order.hub_pickup_at,
        agentName: getAgentName(order.hub_pickup_by_agent_id),
      }))
    : [{
        id: 'hub_pickup_confirmation',
        label: 'Hub-pickup confirmation',
        url: null,
        shouldExist: currentIdx >= STATUS_ORDER.indexOf('out_for_delivery') || isTerminal,
        timestamp: order.hub_pickup_at,
        agentName: getAgentName(order.hub_pickup_by_agent_id),
      }];

  // The agent captures pickup photos per item (garment_tags[].photo_url, signed).
  // Combine the overall pickup photo with those so admin sees ALL pickup proof.
  const garmentPhotos: string[] = (order.garment_tags || [])
    .map((t) => t.photo_url)
    .filter((u): u is string => Boolean(u));
  const pickupPhotoList: string[] = [pickupPhotoUrl, ...garmentPhotos].filter((u): u is string => Boolean(u));
  const pickupProofs: ProofDef[] = pickupPhotoList.length
    ? pickupPhotoList.map((u, i) => ({
        id: `pickup_photo_${i}`,
        label: pickupPhotoList.length > 1 ? `Pickup photo ${i + 1}` : 'Pickup photo',
        url: u,
        shouldExist: true,
        timestamp: pickupDelivery?.completed_at,
        agentName: getAgentName(pickupDelivery?.agent_id, pickupDelivery?.agent_name),
      }))
    : [{
        id: 'pickup_photo',
        label: 'Pickup photo',
        url: null,
        shouldExist: currentIdx >= STATUS_ORDER.indexOf('picked_up') || isTerminal,
        timestamp: pickupDelivery?.completed_at,
        agentName: getAgentName(pickupDelivery?.agent_id, pickupDelivery?.agent_name),
      }];

  const proofs: ProofDef[] = [
    ...pickupProofs,
    {
      id: 'hub_drop_photo',
      label: 'Hub-drop photo',
      url: order.hub_drop_photo_url,
      shouldExist: currentIdx >= STATUS_ORDER.indexOf('at_facility') || isTerminal && !!order.hub_drop_photo_url,
      timestamp: order.updated_at,
      agentName: getAgentName(pickupDelivery?.agent_id, pickupDelivery?.agent_name)
    },
    ...hubPickupProofs,
    {
      id: 'signature',
      label: 'Delivery signature',
      url: order.signature_url,
      shouldExist: currentIdx >= STATUS_ORDER.indexOf('delivered') || isTerminal && !!order.signature_url,
      timestamp: dropDelivery?.completed_at,
      agentName: getAgentName(dropDelivery?.agent_id, dropDelivery?.agent_name),
      isInlineOnly: true
    },
    {
      id: 'cod_photo',
      label: 'COD photo',
      url: order.cod_photo_url,
      shouldExist: (currentIdx >= STATUS_ORDER.indexOf('delivered') && String(order.payment_mode).toLowerCase() === 'cod') || isTerminal && !!order.cod_photo_url,
      timestamp: dropDelivery?.completed_at,
      agentName: getAgentName(dropDelivery?.agent_id, dropDelivery?.agent_name)
    }
  ];

  const visibleProofs = proofs.filter(p => p.shouldExist);
  const clickableProofs = visibleProofs.filter(p => p.url && !p.isInlineOnly);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (lightboxIndex === null) return;
    if (e.key === 'Escape') setLightboxIndex(null);
    if (e.key === 'ArrowLeft') setLightboxIndex(prev => (prev! > 0 ? prev! - 1 : clickableProofs.length - 1));
    if (e.key === 'ArrowRight') setLightboxIndex(prev => (prev! < clickableProofs.length - 1 ? prev! + 1 : 0));
  }, [lightboxIndex, clickableProofs.length]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (visibleProofs.length === 0) {
    return null;
  }

  return (
    <section style={{ marginBottom: '24px', padding: '0 24px' }} aria-label="Proofs Section">
      <p
        className="font-body"
        style={{
          margin: '0 0 12px',
          fontSize: '11px',
          fontWeight: 600,
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        Proofs & Attachments
      </p>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: '8px' }}>
        {visibleProofs.map(proof => {
          if (!proof.url) {
            return (
              <div 
                key={proof.id} 
                style={{
                  border: `1px dashed ${colors.border}`, 
                  borderRadius: '8px', 
                  padding: '8px', 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: colors.bgSecondary,
                  height: '80px',
                  textAlign: 'center'
                }}
              >
                <span style={{ fontSize: '11px', fontWeight: 500, color: colors.textMuted, marginBottom: '4px' }}>{proof.label}</span>
                <span style={{ fontSize: '10px', color: colors.textMuted }}>No proof captured</span>
                {proof.timestamp ? <span style={{ fontSize: '10px', color: colors.textMuted }}>{formatRelativeTime(proof.timestamp)}</span> : null}
                {proof.agentName ? <span style={{ fontSize: '10px', color: colors.textMuted }}>by Agent {proof.agentName}</span> : null}
              </div>
            );
          }

          const clickableIndex = clickableProofs.findIndex(p => p.id === proof.id);

          return (
            <div
              key={proof.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                ...(proof.isInlineOnly ? { gridColumn: '1 / -1' } : {}),
              }}
            >
              {proof.isInlineOnly ? (
                <div
                  style={{
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    padding: '8px',
                    overflow: 'hidden',
                    background: '#fff',
                    width: '100%',
                    maxWidth: '420px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img src={proof.url} alt={proof.label} style={{ width: '100%', maxHeight: '240px', objectFit: 'contain' }} />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setLightboxIndex(clickableIndex)}
                  aria-label={`View ${proof.label} full size`}
                  style={{ border: `1px solid ${colors.border}`, borderRadius: '8px', padding: 0, overflow: 'hidden', background: 'transparent', cursor: 'pointer', width: '80px', height: '80px' }}
                >
                  <img src={proof.url} alt={proof.label} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </button>
              )}
              <div style={{ marginTop: '4px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: colors.textPrimary }}>{proof.label}</div>
                {proof.timestamp && <div style={{ fontSize: '10px', color: colors.textMuted }}>{formatRelativeTime(proof.timestamp)}</div>}
                {proof.agentName && <div style={{ fontSize: '10px', color: colors.textMuted }}>by Agent {proof.agentName}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {lightboxIndex !== null && clickableProofs[lightboxIndex] && (
        <div 
          role="dialog"
          aria-modal="true"
          aria-label="Image Lightbox"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setLightboxIndex(null)}
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setLightboxIndex(null)}
              aria-label="Close Lightbox"
              style={{ position: 'absolute', top: '-40px', right: 0, background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}
            >
              <X size={24} />
            </button>

            {clickableProofs.length > 1 && (
              <button
                onClick={() => setLightboxIndex(prev => (prev! > 0 ? prev! - 1 : clickableProofs.length - 1))}
                aria-label="Previous Image"
                style={{ position: 'absolute', left: '-50px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}
              >
                <ChevronLeft size={32} />
              </button>
            )}

            <img 
              src={clickableProofs[lightboxIndex].url!} 
              alt={clickableProofs[lightboxIndex].label}
              style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
            />

            {clickableProofs.length > 1 && (
              <button
                onClick={() => setLightboxIndex(prev => (prev! < clickableProofs.length - 1 ? prev! + 1 : 0))}
                aria-label="Next Image"
                style={{ position: 'absolute', right: '-50px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}
              >
                <ChevronRight size={32} />
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
